import axios from '@/plugins/axios';
import utils from '@/stores/utils';
import store from '@/stores/store';
import { resolvePermissionCheck } from '@/utils/groupPermissions';

const state = {
    receipt_id: null,
    receiptitem: {},
    // Optional input unit for the count (movement vocabulary); converted
    // server-side.
    quantity_unit: null,
    receiptitem_saving: false,
    receiptitem_saving_errors: {},

    product_id: null,
    provider_id: null,

    receipts: [],
    receipts_count: 0,
    receipts_filters: { limit: 1000, exclude_readonly: true, ready: false, ordering: "receipt_date" },
    receipts_loading: false,
    receipts_loading_errors: null,
};

const buildReceiptsFilters = (provider_id = null) => {
    const filters = {
        exclude_readonly: true,
        status: "draft",
        ready: false,
        ordering: "receipt_date",
        limit: 1000,
    };
    if (provider_id != null && provider_id !== "") {
        filters.provider = provider_id;
    }
    return filters;
};

const resolveProviderId = (params = {}) => {
    if (params.provider_id != null && params.provider_id !== "") {
        return params.provider_id;
    }
    const productData = params.receiptitem?.product_data;
    if (productData?.provider != null) {
        return productData.provider;
    }
    const receiptData =
        params.receiptitem?.receipt_data || params.receipt;
    if (receiptData?.provider != null) {
        return receiptData.provider;
    }
    return null;
};

const mutations = {
    updateProduct(state, product) {
        state.product_id = product;
        state.receipts = [];
        state.receipts_count = 0;
        state.receipts_filters = buildReceiptsFilters(state.provider_id);
    },
    updateReceiptItem(state, payload) {
        const receiptitem = payload?.receiptitem ?? payload;
        const provider_id =
            payload?.provider_id !== undefined
                ? payload.provider_id
                : state.provider_id;

        state.receipts = [];
        state.receipts_count = 0;
        state.receiptitem = receiptitem;
        state.quantity_unit = null;
        state.product_id = receiptitem.product;
        state.provider_id = provider_id;
        state.receipt_id = receiptitem.receipt;
        state.receipts_filters = buildReceiptsFilters(provider_id);
    },
    updateQuantityUnit(state, quantity_unit) {
        state.quantity_unit = quantity_unit || null;
    },
    updateReceiptId(state, receipt_id) {
        state.receiptitem.receipt = receipt_id;
        state.receipt_id = receipt_id;
    },
    updateReceiptItemProduct(state, productId) {
        state.receiptitem.product = productId;
    },
    updateReceiptItemSaving(state, saving) {
        state.receiptitem_saving = saving;
    },
    updateReceiptItemSavingErrors(state, errors) {
        state.receiptitem_saving_errors = errors;
    },
    updateReceipts(state, receipts) {
        state.receipts = receipts;
    },
    updateReceiptsFilters(state, filters) {
        state.receipts_filters = filters;
    },
    updateReceiptsCount(state, count) {
        state.receipts_count = count;
    },
    updateReceiptsLoading(state, loading) {
        state.receipts_loading = loading;
    },
    updateReceiptsLoadingErrors(state, errors) {
        state.receipts_loading_errors = errors;
    },
};

const actions = {

    async fetchReceipts({ commit, state, rootGetters }) {
        if (rootGetters['session/current_user_permissions'].indexOf("core.view_receipt") === -1) {
            return;
        }

        commit('updateReceiptsLoading', true);
        commit('updateReceiptsLoadingErrors', null);

        try {
            const response = await axios.get('/api/receipts/', {
                params: {
                    ...state.receipts_filters,
                    for_product: state.product_id,
                },
            });
            commit('updateReceipts', response.data.results);
            commit('updateReceiptsCount', response.data.count);
            return response.data;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit('updateReceiptsLoadingErrors', error.details);
            throw error;
        } finally {
            commit('updateReceiptsLoading', false);
        }
    },



    async saveReceiptItem({ commit, state, rootGetters }) {
        const permissions = rootGetters['session/current_user_permissions'];
        const hasAddPermission = resolvePermissionCheck(permissions, "core.add_receiptitem");
        const hasChangePermission = resolvePermissionCheck(permissions, "core.change_receiptitem");

        if ((!state.receiptitem.id && !hasAddPermission) || (state.receiptitem.id && !hasChangePermission)) {
            return;
        }

        commit('updateReceiptItemSaving', true);
        commit('updateReceiptItemSavingErrors', {});

        try {
            let url = '/api/receiptitems/';
            let method = axios.post;

            if (state.receiptitem.id) {
                url = `/api/receiptitems/${state.receiptitem.id}/`;
                method = axios.put;
            }
            const payload = { ...state.receiptitem };
            if (state.quantity_unit) {
                payload.quantity_unit = state.quantity_unit;
            }
            const response = await method(url, payload);
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit('updateReceiptItemSavingErrors', error.details);
            throw error;
        } finally {
            commit('updateReceiptItemSaving', false);
        }
    },

    async init({ commit, state, dispatch }, params) {
        const provider_id = resolveProviderId(params);
        await commit('updateReceiptItem', {
            receiptitem: params.receiptitem,
            provider_id,
        });
        if (!(state.receiptitem?.receipt || state.receiptitem?.id)) {
            const receipts = await dispatch('fetchReceipts');
            if (receipts?.count) {
                await commit('updateReceiptId', receipts.results[0].id);
            }
        }
    }
};


export default {
    namespaced: true,
    state,
    actions,
    mutations,
};
